import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DeactivatableButton,
  FormBuilder,
} from '@folio/components';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  useInvitationControllerBrokerInvite,
} from 'korint-sdk';
import { useKorintModuleConfig, type Field } from 'korint-sdk/react';
import { Send } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';

export function IntermediaryUserCreationModule() {
  const { t } = useTranslation();
  const { data: user, isLoading: isUserLoading } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const form = useForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      brokerRole: '',
    },
  });

  const intermediaryUserCreationConfig = useKorintModuleConfig('intermediaryUserCreation');

  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user?.brokerageFirmId!),
    enabled: !!user?.brokerageFirmId,
  });

  const { mutateAsync: inviteBroker, isPending: isInviteLoading } =
    useInvitationControllerBrokerInvite();

  // Determine available roles based on firm type
  const availableRoles = useMemo<string[]>(() => {
    if (!brokerageFirm?.firmType) return [];

    const rolesForFirmType =
      intermediaryUserCreationConfig.inviteRole[
        brokerageFirm.firmType as keyof typeof intermediaryUserCreationConfig.inviteRole
      ];

    // Support both string (legacy) and array (new) formats
    if (Array.isArray(rolesForFirmType)) {
      return rolesForFirmType;
    }

    // Legacy: single string role
    return rolesForFirmType ? [rolesForFirmType] : [];
  }, [intermediaryUserCreationConfig, brokerageFirm]);

  // Auto-select if single role
  useEffect(() => {
    if (availableRoles.length === 1) {
      form.setValue('brokerRole', availableRoles[0]);
    }
  }, [availableRoles, form]);

  // Build fields array dynamically
  const fields = useMemo<Array<Field>>(() => {
    const baseFields: Array<Field> = [
      {
        name: 'email',
        type: 'email',
        required: true,
      },
      {
        name: 'firstName',
        type: 'text',
        required: true,
      },
      {
        name: 'lastName',
        type: 'text',
        required: true,
      },
    ];

    // Only show role selector if multiple roles available
    if (availableRoles.length > 1) {
      baseFields.push({
        name: 'brokerRole',
        type: 'select',
        required: true,
        DEPRECATED_placeholder: t('intermediaryUserCreation.roleFieldPlaceholder'),
        options: availableRoles.map(role => ({
          label: t(`businessRoles.${role}.label`),
          value: role,
        })),
      });
    }

    return baseFields;
  }, [availableRoles, t]);

  // Get the final role value
  const finalRole = form.watch('brokerRole');

  return (
    <Stack>
      <Card>
        <CardHeader>
          <CardTitle>{t('intermediaryUserCreation.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <FormBuilder fields={fields} form={form} isLoading={isUserLoading || isInviteLoading} />
          <Group align="stretch" justify="end" className="mt-6">
            <DeactivatableButton
              type="submit"
              leftIcon={<Send className="h-4 w-4" />}
              rules={[
                {
                  isValid: !!finalRole && availableRoles.includes(finalRole),
                  message:
                    availableRoles.length === 0
                      ? t('intermediaryUserCreation.noRolesConfigured')
                      : t('intermediaryUserCreation.selectRoleRequired'),
                },
              ]}
              onClick={form.handleSubmit(async data => {
                if (!user?.brokerageFirmId) return;

                await inviteBroker({
                  data: {
                    email: data.email,
                    brokerageFirmId: user.brokerageFirmId,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    brokerRole: finalRole,
                  },
                });

                form.reset({
                  email: '',
                  firstName: '',
                  lastName: '',
                  brokerRole: availableRoles.length === 1 ? availableRoles[0] : '',
                });
              })}
            >
              {t('intermediaryUserCreation.inviteButton')}
            </DeactivatableButton>
          </Group>
        </CardContent>
      </Card>
    </Stack>
  );
}
